import { useQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import Spinner from '@/components/Common/Spinner';
import TreeSelect from '@/components/Select/TreeSelect';
import feedbackPropertyFetcher from '@/features/(charts)/chart-controller/fetchers/useFeedbackPropertyFetcher';
import Label from '@/features/Feedback/field/Tag/Label';
import service from '@/services/index';
import { ROOT_TAG_ALIAS } from '@/services/tag/converters';
import useTagStore from '@/stores/TagStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import {
  WORKFLOW_PERIODIC_BASIS,
  WorkflowPeriodicBasisDic,
} from '@/types/workflowConst';

type Props = {
  basis?: WORKFLOW_PERIODIC_BASIS;
  value: string[];
  onChange: (values: string[]) => void;
};

const SpecificPeriodicBasisSelect = (props: Props) => {
  const workspaceFieldOptions = useWorkspaceStore(
    (state) => state.workspaceFieldOptions,
  );
  const tagMap = useTagStore((state) => state.tagMap);

  const [searchInput, setSearchInput] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const getOptionsByFieldOptions = useCallback(
    (fieldName: keyof WorkspaceFieldOptions) => {
      return workspaceFieldOptions[fieldName].map((value) => ({
        value: value,
        label: value,
      }));
    },
    [workspaceFieldOptions],
  );

  const isAsyncBasis = useMemo(() => {
    const asyncBasisList = [WORKFLOW_PERIODIC_BASIS.REMARKS];
    if (!props.basis) return false;
    return asyncBasisList.includes(props.basis);
  }, [props.basis]);

  const fetchData = async (
    enabled: boolean,
    basis: WORKFLOW_PERIODIC_BASIS | undefined,
    searchString: string,
  ) => {
    if (!enabled) return [];
    if (!basis) return [];
    if (basis === WORKFLOW_PERIODIC_BASIS.REMARKS) {
      if (searchString.trim() === '') return [];
      const res = await service.feedback.searchRemarks(searchString);
      return res.remarks.map((value) => ({
        value: value,
        label: value,
      }));
    }

    throw new Error('INVALID BASIS');
  };

  const { data: asyncData, isLoading } = useQuery({
    queryKey: ['periodic-select-basis', props.basis, searchValue],
    queryFn: () => fetchData(isAsyncBasis, props.basis, searchValue),
    enabled: isAsyncBasis,
  });

  const options = useMemo(() => {
    if (isAsyncBasis) return asyncData;
    switch (props.basis) {
      case WORKFLOW_PERIODIC_BASIS.DATA_ORIGIN:
        return getOptionsByFieldOptions('data_origin_values');
      case WORKFLOW_PERIODIC_BASIS.TAG:
        return Object.entries(tagMap)
          .filter(([alias]) => alias !== ROOT_TAG_ALIAS)
          .map(([alias, tag]) => {
            let textArr: string[] = [];
            const getParentName = (tag: TagT) => {
              if (tag.parent && tag.parent !== ROOT_TAG_ALIAS) {
                const parent = tagMap[tag.parent];
                textArr.unshift(parent.name);
                getParentName(parent);
              }
            };
            getParentName(tag);
            return {
              value: alias,
              label: (
                <div
                  className="flex h-full items-center justify-between gap-0.5 pr-4"
                >
                  <div className="flex flex-1 items-center">
                    <Label
                      style={{
                        paddingLeft: 0,
                      }}
                      withoutBorder
                      withoutHover
                      text={tag.name}
                      themeColor={tag.color}
                    />
                  </div>
                  <span className="truncate text-xs text-muted-foreground">
                    {textArr.join(' / ')}
                  </span>
                </div>
              ),
              searchName: tag.name,
            };
          });
      default:
        return [];
    }
  }, [
    asyncData,
    getOptionsByFieldOptions,
    isAsyncBasis,
    props.basis,
    tagMap,
  ]);

  const debounceChange = useDebouncedCallback((newValue: string) => {
    setSearchValue(newValue);
  }, 300);

  const handleSearch = (newValue: string) => {
    setSearchInput(newValue);
    debounceChange(newValue);
  };

  const taxonomyFetcher = feedbackPropertyFetcher.useFetchTaxonomyTree();

  if (!props.basis) return null;

  if (props.basis === WORKFLOW_PERIODIC_BASIS.TOPIC)
    return (
      <TreeSelect
        style={{ width: 300 }}
        mode="single"
        values={props.value}
        onChange={(values) => props.onChange(values.map((v) => v.toString()))}
        searchable
        fetcher={taxonomyFetcher}
      />
    );

  // NOTE: antd Select mode="multiple"는 복잡한 검색/필터 로직으로 인해 유지
  return (
    <Select
      mode="multiple"
      showSearch
      filterOption={
        isAsyncBasis
          ? false
          : (value, option) => {
              const casted = option as {
                value: string;
                label: string;
                searchName?: string;
              };
              return !!casted.searchName?.includes(value) || false;
            }
      }
      value={props.value}
      searchValue={searchInput}
      options={
        options as {
          value: string;
          label: ReactNode;
        }[]
      }
      onChange={props.onChange}
      onSearch={handleSearch}
      style={{ width: 300 }}
      notFoundContent={
        isLoading ? (
          <div className="flex h-12 items-center justify-center">
            <Spinner size={12} />
          </div>
        ) : (
          <div className="flex h-12 items-center justify-center">
            <span className="text-sm text-muted-foreground">No data</span>
          </div>
        )
      }
      placeholder={`Select ${WorkflowPeriodicBasisDic[props.basis]}`}
    />
  );
};

export default SpecificPeriodicBasisSelect;
